/**
 * src/lib/button-spinner.ts — reusable button spinner helper.
 *
 * Eliminuje klasę bugów "spinner zawiesił się na zawsze" przez:
 *   - twardy timeout (default 60s) z AbortController.signal przekazywanym do asyncFn
 *   - re-click guard (`_inProgress` na elemencie)
 *   - ZAWSZE restore innerHTML w finally{} (nawet jeśli showToast/render rzuci)
 *   - hydratacja ikon Lucide po każdym przełączeniu
 *
 * Użycie:
 *   await withButtonSpinner(btn, { idleIcon: 'save', idleText: 'Zapisz' }, async (signal) => {
 *     const res = await fetch('/api/save', { signal, method: 'POST', body });
 *     if (!res.ok) throw new Error('HTTP ' + res.status);
 *     return await res.json();
 *   });
 *
 * Globale używane przez helper (dostępne via window):
 *   - window.lucide.createIcons({nodes:[el]})  — hydratacja ikon
 *   - window.showToast(msg, type)              — toast notyfikacje
 */

export interface ButtonSpinnerOptions {
  /** Lucide icon name when idle (default: existing icon stays — only text restored). */
  idleIcon?: string;
  /** Text shown during action (default: "Ładowanie..."). */
  loadingText?: string;
  /** Text shown when idle (default: original textContent restored from innerHTML snapshot). */
  idleText?: string;
  /** Hard timeout in ms (default: 60_000 = 60s). */
  timeoutMs?: number;
  /** Toast message on timeout. */
  timeoutToast?: string;
  /** Toast message prefix on error. */
  errorToastPrefix?: string;
  /** If false, suppress error toast (caller handles). Default true. */
  showErrorToast?: boolean;
}

interface ButtonStateBag {
  _inProgress?: boolean;
  _sbOriginalHTML?: string;
}

const DEFAULT_TIMEOUT_MS = 60_000;
const DEFAULT_LOADING_TEXT = 'Ładowanie...';
const DEFAULT_TIMEOUT_TOAST = 'Operacja przekroczyła limit czasu';
const DEFAULT_ERROR_PREFIX = 'Błąd: ';

function safeShowToast(msg: string, type: 'success' | 'error' | 'info' | 'warning' = 'error'): void {
  try {
    const w = window as unknown as { showToast?: (m: string, t?: string) => void };
    if (typeof w.showToast === 'function') {
      w.showToast(msg, type);
    } else {
      // eslint-disable-next-line no-console
      console.warn('[button-spinner] showToast not available:', msg);
    }
  } catch (_e) {
    /* swallow — never let toast errors break finally */
  }
}

function hydrateIcons(node: HTMLElement): void {
  try {
    const w = window as unknown as { lucide?: { createIcons?: (opts: { nodes: HTMLElement[] }) => void } };
    if (w.lucide && typeof w.lucide.createIcons === 'function') {
      w.lucide.createIcons({ nodes: [node] });
    }
  } catch (_e) {
    /* swallow — icon hydration errors should not break action */
  }
}

function buildSpinnerHTML(loadingText: string): string {
  // loader-2 + animate-spin = spinning loader icon. Inline w-4 h-4 mr-2 align-middle.
  return '<i data-lucide="loader-2" class="w-4 h-4 mr-2 inline-block animate-spin align-middle"></i>' +
    escapeHtml(loadingText);
}

function buildIdleHTML(idleIcon: string | undefined, idleText: string | undefined, fallback: string): string {
  if (!idleIcon && !idleText) return fallback;
  const iconPart = idleIcon
    ? '<i data-lucide="' + escapeAttr(idleIcon) + '" class="w-4 h-4 mr-2 inline-block align-middle"></i>'
    : '';
  const textPart = idleText !== undefined ? escapeHtml(idleText) : '';
  return iconPart + textPart;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(s: string): string {
  return s.replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

/**
 * Wraps an async button action with a spinner, hard timeout, re-click guard and
 * guaranteed restore in finally{}. Returns asyncFn's resolved value, or undefined
 * on timeout / error / re-click.
 */
export async function withButtonSpinner<T>(
  btn: HTMLElement | null,
  options: ButtonSpinnerOptions,
  asyncFn: (signal: AbortSignal) => Promise<T>
): Promise<T | undefined> {
  const opts: Required<ButtonSpinnerOptions> = {
    idleIcon: options.idleIcon ?? '',
    loadingText: options.loadingText ?? DEFAULT_LOADING_TEXT,
    idleText: options.idleText ?? '',
    timeoutMs: options.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    timeoutToast: options.timeoutToast ?? DEFAULT_TIMEOUT_TOAST,
    errorToastPrefix: options.errorToastPrefix ?? DEFAULT_ERROR_PREFIX,
    showErrorToast: options.showErrorToast ?? true,
  };

  const controller = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let timedOut = false;

  // No button → still run asyncFn with signal (e.g. programmatic invocation).
  if (!btn) {
    try {
      timeoutId = setTimeout(() => {
        timedOut = true;
        try { controller.abort(); } catch (_e) { /* noop */ }
      }, opts.timeoutMs);
      const result = await asyncFn(controller.signal);
      return result;
    } catch (err: unknown) {
      if (timedOut) {
        if (opts.showErrorToast) safeShowToast(opts.timeoutToast, 'error');
      } else if (opts.showErrorToast) {
        const msg = err instanceof Error ? err.message : String(err);
        safeShowToast(opts.errorToastPrefix + msg, 'error');
      }
      return undefined;
    } finally {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    }
  }

  const bag = btn as unknown as ButtonStateBag;

  // Re-click guard.
  if (bag._inProgress === true) {
    return undefined;
  }

  // Snapshot original innerHTML. If a previous run was interrupted mid-restore
  // and left a snapshot, prefer the existing snapshot to avoid capturing the spinner.
  const originalHTML = bag._sbOriginalHTML !== undefined ? bag._sbOriginalHTML : btn.innerHTML;
  bag._sbOriginalHTML = originalHTML;

  bag._inProgress = true;
  const btnAsDisableable = btn as HTMLElement & { disabled?: boolean };
  const wasDisabled = btnAsDisableable.disabled === true;
  btnAsDisableable.disabled = true;

  try {
    btn.innerHTML = buildSpinnerHTML(opts.loadingText);
    hydrateIcons(btn);

    timeoutId = setTimeout(() => {
      timedOut = true;
      try { controller.abort(); } catch (_e) { /* noop */ }
    }, opts.timeoutMs);

    const result = await asyncFn(controller.signal);
    return result;
  } catch (err: unknown) {
    if (timedOut) {
      if (opts.showErrorToast) safeShowToast(opts.timeoutToast, 'error');
    } else if (opts.showErrorToast) {
      const msg = err instanceof Error ? err.message : String(err);
      safeShowToast(opts.errorToastPrefix + msg, 'error');
    }
    return undefined;
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
    try {
      // Restore: prefer explicit idleIcon/idleText if caller wants a new label,
      // otherwise put back the original innerHTML verbatim.
      if (opts.idleIcon || options.idleText !== undefined) {
        btn.innerHTML = buildIdleHTML(opts.idleIcon, options.idleText, originalHTML);
      } else {
        btn.innerHTML = originalHTML;
      }
      hydrateIcons(btn);
    } catch (_e) {
      /* DOM gone (button removed during action)? Nothing to restore. */
    }
    btnAsDisableable.disabled = wasDisabled;
    bag._inProgress = false;
    // Clear snapshot so next call recaptures fresh innerHTML.
    delete bag._sbOriginalHTML;
  }
}
